/**
 * One silent retry of a Microsoft sign-in whose handshake failed on our side.
 *
 * The backend sends `/login?error=sso_retry` when the callback could not
 * complete for a reason the person did not cause: the sign-in page sat open
 * past the 10-minute state cookie, a second tab started another sign-in, or
 * Back replayed an old callback (LIVE, 30 Sep). Microsoft still has them signed
 * in, so starting again finishes in a second with nothing to click.
 *
 * The guard lives here, not on the server, because the server's own memory of
 * the attempt is the cookie that just failed. One retry per `RETRY_WINDOW_MS`;
 * when storage is unavailable there is no retry at all — a browser that blocks
 * storage likely blocks the cookie too, and a loop through Microsoft is worse
 * than the error message.
 *
 * Pure and import-free so `node --test` can load it straight from source.
 */

export const SSO_RETRY_ERROR = "sso_retry";
export const RETRY_WINDOW_MS = 60_000;
const STORAGE_KEY = "it-helpdesk-agent.sso-retry-at";

/** The storage this needs; `sessionStorage` in the browser. */
export interface RetryStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * Whether to retry now, recording the attempt when it says yes.
 *
 * Args: the store (or null when there is none), and the current time in ms.
 */
export function claimSsoRetry(store: RetryStore | null, now: number): boolean {
  if (store === null) {
    return false;
  }
  try {
    const last = Number(store.getItem(STORAGE_KEY) ?? "");
    if (Number.isFinite(last) && last > 0 && now - last < RETRY_WINDOW_MS) {
      return false;
    }
    store.setItem(STORAGE_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}

/** `/login?signed_out=1`: the person just signed out, so the page waits for a click. */
export const SIGNED_OUT_PARAM = "signed_out";

/**
 * Whether the login page goes straight to Microsoft (28 Oct, Rusty: "users go
 * straight in without having to click the button"). Not after a failed
 * sign-in (the page explains it), not straight after signing out (it would
 * sign them back in at once), and not on the local dev picker.
 */
export function shouldAutoSignIn(params: URLSearchParams, devPicker: boolean): boolean {
  return !devPicker && !params.get("error") && !params.get(SIGNED_OUT_PARAM);
}
